import type * as am4charts from "@amcharts/amcharts4/charts";
import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import {
    ProfitLossChartDataSeriesCalculatedIdentifier,
    ProfitLossChartReportDto,
    ProfitLossReportDto,
} from "@common/dto/reports/reporting-tab-profit-loss-response.dto";
import { printLabel } from "@common/helpers/reports";
import { ReportFilters } from "@common/types/filters/reports";
import { isUUID } from "@common/helpers/uuid";
import {
    CategoryLabelGetter,
    useCategoryLabelGetter,
} from "@hooks/useCategoryLabelGetter";
import { useStandardCategories } from "@hooks/useStandardCategories";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { AmchartsModules, getAmcharts } from "@lib/loaders/amchartsLoader";
import { Card } from "@design-system/Card/Card";
import {
    ChartDataSeriesItem,
    CreateFilteredChartParams,
    GenerateDataSeriesParams,
} from "./types";
import { generateSingleCategoryTooltip } from "./helpers/tooltip";
import { applySeriesColor } from "./helpers/colors";

const chartLabelsMap: Record<
    ProfitLossChartDataSeriesCalculatedIdentifier,
    string
> = {
    other: "Other",
    income_rest: "Other",
    dummy: "Dummy",
};

function getSeriesLabel(
    field: ChartDataSeriesItem,
    getCategoryLabel: CategoryLabelGetter,
): string {
    if (
        Object.values(ProfitLossChartDataSeriesCalculatedIdentifier).includes(
            field.label as ProfitLossChartDataSeriesCalculatedIdentifier,
        )
    ) {
        return chartLabelsMap[
            field.label as ProfitLossChartDataSeriesCalculatedIdentifier
        ];
    }

    if (isUUID(field.label)) {
        return getCategoryLabel(field.label);
    }

    return field.label;
}

function generateDataSeries({
    chart,
    item,
    getCategoryLabel,
    categoriesById,
    amcharts,
}: GenerateDataSeriesParams) {
    const { am4core, am4charts } = amcharts;

    const series = chart.series.push(new am4charts.ColumnSeries());
    series.name = getSeriesLabel(item, getCategoryLabel);

    series.dataFields.valueY = item.label;
    series.dataFields.categoryX = "label";

    series.stacked = true;
    series.strokeWidth = 0.5;

    applySeriesColor({ item, series, categoriesById, amcharts });
    series.strokeOpacity = 0.6;
    series.fillOpacity = 0.6;

    series.columns.template.width = am4core.percent(45);

    series.columns.template.tooltipHTML = ``;
    series.columns.template.tooltipY = am4core.percent(50);

    return series;
}

interface CreateCashFlowChartParams extends CreateFilteredChartParams<ProfitLossChartReportDto> {
    reportCreatedAt: number;
    setCategoryFilter?: (categories: string[]) => void;
    amcharts: AmchartsModules;
}

function createChart({
    id,
    report,
    getCategoryLabel,
    filters,
    categoriesById,
    reportCreatedAt,
    setCategoryFilter,
    amcharts,
}: CreateCashFlowChartParams) {
    const { am4core, am4charts } = amcharts;

    const chart = am4core.create(id, am4charts.XYChart);

    chart.data = report.data;
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "label";
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.cursorTooltipEnabled = false;
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.labels.template.fill = am4core.color("#515d71");
    categoryAxis.renderer.labels.template.adapter.add("textOutput", (label) =>
        printLabel(label, filters),
    );
    categoryAxis.renderer.minLabelPosition = 0.02;
    categoryAxis.renderer.maxLabelPosition = 0.98;
    if (chart.data.length <= 12) {
        categoryAxis.renderer.minGridDistance = 1;
    }

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.grid.template.stroke = am4core.color("#53647e");
    valueAxis.renderer.grid.template.opacity = 0.6;
    valueAxis.renderer.baseGrid.stroke = am4core.color("#53647e");
    valueAxis.renderer.baseGrid.opacity = 0.7;
    valueAxis.renderer.baseGrid.strokeWidth = 1.5;
    valueAxis.renderer.labels.template.fill = am4core.color("#515d71");
    valueAxis.renderer.labels.template.fontSize = 12;
    valueAxis.cursorTooltipEnabled = false;

    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "$#,###sa| -$#,###sa";

    const series: am4charts.ColumnSeries[] = [];
    const reportSeries = report.series.filter(
        (s) => s.label !== ProfitLossChartDataSeriesCalculatedIdentifier.DUMMY,
    );

    for (const rs of reportSeries) {
        series.push(
            generateDataSeries({
                chart,
                item: rs,
                getCategoryLabel,
                categoriesById,
                amcharts,
            }),
        );
    }
    for (const seriesItem of series) {
        generateSingleCategoryTooltip(
            {
                chart,
                seriesToHookInto: seriesItem,
                filters,
                categoriesById,
                amcharts,
            },
            reportCreatedAt,
        );
    }

    const columnSeries = series;
    for (const seriesItem of series) {
        if (setCategoryFilter) {
            seriesItem.columns.template.cursorOverStyle =
                am4core.MouseCursorStyle.pointer;
            seriesItem.columns.template.events.on("hit", function () {
                const category = seriesItem.dataFields.valueY!;

                if (category !== "other") {
                    if (isUUID(category)) {
                        setCategoryFilter([category]);
                        return;
                    }

                    return;
                }

                const categories: string[] =
                    filters.category ??
                    Object.values(categoriesById).map((cat) => cat.id);
                const shownCategoryIds = new Set(
                    columnSeries
                        .filter((item) => item !== seriesItem)
                        .map((item) => item.dataFields.valueY),
                );
                const selectedTaxonomies = categories.filter(
                    (cat) => !shownCategoryIds.has(cat),
                );
                setCategoryFilter(selectedTaxonomies);
            });
        }
    }

    chart.padding(30, 24, 12, 12);

    return chart;
}

export interface CashFlowReportChartProps {
    report: ProfitLossReportDto;
    filters: ReportFilters;
    reportCreatedAt: number;
    setCategoryFilter?: (categories: string[]) => void;
}

export const CashFlowReportChart: React.FC<CashFlowReportChartProps> = ({
    report,
    filters,
    reportCreatedAt,
    setCategoryFilter,
}) => {
    const getCategoryLabel = useCategoryLabelGetter();
    const categoryMap = useStandardCategories();
    const categoriesById = useCategoriesById();

    useEffect(() => {
        let isDisposed = false;
        let chart: am4charts.XYChart | undefined;

        getAmcharts()
            .then((amcharts) => {
                if (isDisposed) {
                    return;
                }
                chart = createChart({
                    id: "cash-flow-chart",
                    report: report.chart,
                    getCategoryLabel,
                    filters,
                    reportCreatedAt,
                    setCategoryFilter,
                    categoriesById,
                    amcharts,
                });
            })
            .catch((error) => {
                Sentry.captureException(error);
            });

        return () => {
            isDisposed = true;
            chart?.dispose();
        };
    }, [
        report.chart,
        getCategoryLabel,
        filters,
        categoryMap,
        reportCreatedAt,
        setCategoryFilter,
        categoriesById,
    ]);
    return (
        <Card className="cash-flow-chart" noBorder sharpEdges>
            <div id="cash-flow-chart" className="cash-flow-chart__chart"></div>
        </Card>
    );
};
